/**
 * @file components/chat/RichResultCard.tsx
 * Carte de réponse enrichie (plateforme ➔ utilisateur) :
 *   - résumé exécutif LLM
 *   - métadonnées extraites (tableau compact)
 *   - actions rapides intégrées à la bulle
 */
import React from 'react';
import { Bot, Download, ShieldCheck, Lock } from 'lucide-react';
import type { ResultCardMessage } from './types.ts';

function formatTime(at: number): string {
  return new Date(at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export default function RichResultCard({
  message,
  onDownloadReport,
  onCheckCompliance,
}: {
  message: ResultCardMessage;
  onDownloadReport?: (dossierId: string) => void;
  onCheckCompliance?: (dossierId: string) => void;
}): React.ReactElement {
  return (
    <div className="flex items-end gap-2">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Bot size={15} />
      </div>
      <div className="w-[85%] max-w-lg rounded-2xl rounded-bl-sm border border-border bg-card px-4 py-3 text-card-foreground shadow-sm">
        {message.restricted ? (
          <div className="flex items-start gap-2.5">
            <Lock size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-foreground">Accès restreint</p>
              <p className="mt-1 text-sm text-muted-foreground">
                La classification de ce document
                {message.classification ? ` (${message.classification})` : ''} dépasse votre
                habilitation. Le résumé et les métadonnées ne peuvent pas être affichés.
              </p>
              <span className="mt-2 block text-[10px] text-muted-foreground">
                {formatTime(message.at)}
              </span>
            </div>
          </div>
        ) : (
          <>
        <p className="status-completed text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          Analyse terminée
        </p>
        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{message.summary}</p>

        {message.metadata.length > 0 && (
          <dl className="mt-3 divide-y divide-border rounded-lg border border-border">
            {message.metadata.map((m) => (
              <div key={m.label} className="flex gap-3 px-3 py-1.5 text-sm">
                <dt className="w-2/5 shrink-0 font-medium text-muted-foreground">{m.label}</dt>
                <dd className="flex-1 break-words">{m.value}</dd>
              </div>
            ))}
          </dl>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onDownloadReport?.(message.dossierId)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <Download size={13} />
            Télécharger le rapport
          </button>
          <button
            type="button"
            onClick={() => onCheckCompliance?.(message.dossierId)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <ShieldCheck size={13} />
            Vérifier la conformité
          </button>
        </div>

        <span className="mt-2 block text-[10px] text-muted-foreground">{formatTime(message.at)}</span>
          </>
        )}
      </div>
    </div>
  );
}
